/**
 * The totals above the decisions list, and the Overview's refusals, in words
 * (DEC-28 C1, C2).
 *
 * THE SERVICE SENDS THE FIELDS AND THIS WORDS THEM, with the very function
 * the list words a row with. One wording in one place, so "Why it was not
 * approved" above the list and the "Why" column under it cannot say the same
 * thing two ways. A group carries no token, so a sentence that would name one
 * says its kind instead.
 */

import type { DecisionSummary, ReasonGroup } from '../api'
import { decidedByLabel, reasonInWordsFor } from './decisionReading'

export interface WhyRow {
  outcome: string
  label: string
  count: number
  /** The authority's own word when every decision in the row shares one, so
   *  a click can filter the list on it. */
  reason: string | null
}

function asDecision(group: ReasonGroup): DecisionSummary {
  return { ...group, identifier_record: 'not_kept', identifier: null } as unknown as DecisionSummary
}

/** The groups worded, the same words added up, the largest first. */
export function whyRows(groups: ReasonGroup[], limit = 10): WhyRow[] {
  const merged = new Map<string, WhyRow>()
  for (const group of groups) {
    if (group.outcome === 'APPROVED') continue
    const label = reasonInWordsFor(asDecision(group))
    const key = `${group.outcome}|${label}`
    const found = merged.get(key)
    if (found) {
      found.count += group.count
      if (found.reason !== group.reason) found.reason = null
    } else {
      merged.set(key, { outcome: group.outcome, label, count: group.count, reason: group.reason })
    }
  }
  return [...merged.values()].sort((a, b) => b.count - a.count).slice(0, limit)
}

export interface DeciderRow {
  decidedBy: string | null
  label: string
  count: number
}

/** Who stated the verdict, as the list's "Decided by" column names them,
 *  over every outcome the totals were asked for. */
export function deciderRows(rows: { decided_by: string | null; count: number }[]): DeciderRow[] {
  const merged = new Map<string, DeciderRow>()
  for (const row of rows) {
    const label = decidedByLabel(row.decided_by)
    const found = merged.get(label)
    if (found) found.count += row.count
    else merged.set(label, { decidedBy: row.decided_by, label, count: row.count })
  }
  return [...merged.values()].sort((a, b) => b.count - a.count)
}

/** A share of a total, as the page writes one: one decimal, or nothing to
 *  divide by. */
export function shareOf(part: number, total: number): string {
  if (!total) return 'nothing to compare'
  return `${(Math.round((part * 1000) / total) / 10).toFixed(1)}% of asks`
}

export function milliseconds(value: number | null | undefined): string {
  return value === null || value === undefined ? 'nothing measured' : `${Math.round(value).toLocaleString('en-GB')} ms`
}

export function count(value: number): string {
  return value.toLocaleString('en-GB')
}
